package com.macrofocus.eflux.network

import com.macrofocus.common.math.clampNormalize
import com.macrofocus.common.units.Kilovolt
import com.macrofocus.common.units.PhaseDegree
import com.macrofocus.eflux.ui.PaletteType
import kotlin.math.max
import kotlin.math.min
import kotlin.reflect.KClass

interface NodeField<T> {
    val name: String

    val longName: String

    val icon: String?

    fun getValue(row: Node): T

    fun getFormattedValue(row: Node): String? = getValue(row)?.toString()

    fun getColor(row: Node, paletteType: PaletteType): String?

    fun getNormalized(row: Node): Double?

    override fun toString(): String

    companion object {
        //        val name = FunctionalNodeField<String?>("NAME", String::class, { it.bus?.buses?.first()?.record?.name })
//        val i = FunctionalNodeField("i", Int::class, { it.bus?.buses?.first()?.record?.i })
        val baskv = FunctionalNodeField(
            "BASKV", Double::class, { it.bus?.primary?.record?.baskv }, null,
            { it, paletteType ->
                val voltage = it.bus?.primary?.record?.baskv
                when {
                    voltage == null -> null
                    voltage >= 380.Kilovolt -> if (paletteType == PaletteType.dark) "#835eac" else "#7346A2"
                    voltage >= 220.Kilovolt -> if (paletteType == PaletteType.dark) "#268d05" else "#17AF97"
                    else -> if (paletteType == PaletteType.dark) "#2a9cf4" else "#0FB2E5"
                }
            },
            { it.bus?.primary?.record?.baskv?.let { min(1.0, it / 440.Kilovolt) } },
            longName = "Voltage level"
        )

        val v = FunctionalNodeField(
            "V", Double::class, { it.bus?.primary?.v },
            { it?.amount?.toFixed(2) + " " + it?.unit?.symbol },
            { it, paletteType ->
                val voltage = it.bus?.primary?.v
                val red = "#e4032e"
                val orange = "#f28200"
                val yellow = "#ffcc00"
                val green = "#17af97"
                when {
                    voltage == null -> null
                    voltage > 421.Kilovolt -> red
                    voltage > 418.Kilovolt -> orange
                    voltage > 416.Kilovolt -> yellow
                    voltage > 399.Kilovolt -> green
                    voltage > 397.Kilovolt -> yellow
                    voltage > 390.Kilovolt -> orange
                    voltage > 360.Kilovolt -> red
                    voltage > 246.Kilovolt -> red
                    voltage > 243.Kilovolt -> orange
                    voltage > 241.Kilovolt -> yellow
                    voltage > 229.Kilovolt -> green
                    voltage > 227.Kilovolt -> yellow
                    voltage > 220.Kilovolt -> orange
                    voltage > 210.Kilovolt -> red
                    else -> null
                }
            },
            { it.bus?.primary?.v?.let { min(1.0, it / 440.Kilovolt) } },
            longName = "Voltage"
        )

        val v2 = FunctionalNodeField(
            "V", Double::class, { it.bus?.primary?.v },
            { it?.amount?.toFixed(2) + " " + it?.unit?.symbol },
            { it, paletteType ->
                val voltage = it.bus?.primary?.v
                val red = "#e4032e"
                val orange = "#f28200"
                val yellow = "#ffcc00"
                val green = null
                when {
                    voltage == null -> null
                    voltage > 421.Kilovolt -> red
                    voltage > 418.Kilovolt -> orange
                    voltage > 416.Kilovolt -> yellow
                    voltage > 399.Kilovolt -> green
                    voltage > 397.Kilovolt -> yellow
                    voltage > 390.Kilovolt -> orange
                    voltage > 360.Kilovolt -> red
                    voltage > 246.Kilovolt -> red
                    voltage > 243.Kilovolt -> orange
                    voltage > 241.Kilovolt -> yellow
                    voltage > 229.Kilovolt -> green
                    voltage > 227.Kilovolt -> yellow
                    voltage > 220.Kilovolt -> orange
                    voltage > 210.Kilovolt -> red
                    else -> null
                }
            },
            { it.bus?.primary?.v?.let { min(1.0, it / 440.Kilovolt) } },
            longName = "Voltage"
        )

        //        val ide = FunctionalNodeField("IDE", Double::class, { it.bus?.buses?.first()?.record?.ide })
//        val area = FunctionalNodeField("AREA", Double::class, { it.bus?.buses?.first()?.record?.area })
//        val zone = FunctionalNodeField("ZONE", Double::class, { it.bus?.buses?.first()?.record?.zone })
//        val owner = FunctionalNodeField("OWNER", Double::class, { it.bus?.buses?.first()?.record?.owner })
        val vm = FunctionalNodeField(
            "VM",
            Double::class,
            { it.bus?.primary?.record?.vm },
            normalized = { it.bus?.primary?.record?.vm?.let { it.amount.clampNormalize(0.95, 1.19) } },
            longName = "Voltage Magnitude"
        )
        val va = FunctionalNodeField(
            "VA",
            Double::class,
            { it.bus?.primary?.record?.va },
            { it?.amount?.toFixed(2) + "" + it?.unit?.symbol },
            { it, paletteType ->
                val va = it.bus?.primary?.record?.va
                when {
                    va == null -> null
                    va > 0.PhaseDegree -> "#fc8d59"
                    va == 0.PhaseDegree -> "#ffffbf"
                    else -> "#91bfdb"
                }
            },
            { it.bus?.primary?.record?.va?.let { (min(15.0, max(-15.0, it.amount / 15.0)) / 2.0) + 0.5 } },
            normalized = { it.bus?.primary?.record?.va?.let { (min(1.0, max(0.0, (it.amount + 180.0) / 360.0))) } },
            longName = "Voltage Angle"
        )
//        val nvhi = FunctionalNodeField("NVHI", Double::class, { it.bus?.buses?.first()?.record?.nvhi })
//        val nvlo = FunctionalNodeField("NVLO", Double::class, { it.bus?.buses?.first()?.record?.nvlo })
//        val evhi = FunctionalNodeField("EVHI", Double::class, { it.bus?.buses?.first()?.record?.evhi })
//        val evlo = FunctionalNodeField("EVLO", Double::class, { it.bus?.buses?.first()?.record?.evlo })

        val all: Array<NodeField<*>> =
            arrayOf(
//                i, name,
                baskv,
                v2
//                ide, area, zone, owner, vm, va, nvhi, nvlo, evhi, evlo
            )

        val tooltip: Array<NodeField<*>> =
            arrayOf(
//                name,
                baskv,
//                ide, area, zone, owner,
//                vm, va,
//                nvhi, nvlo, evhi, evlo
            )

        val color: Array<NodeField<*>> =
            arrayOf(
                baskv,
                v2
            )

        val size: Array<NodeField<*>> =
            arrayOf(
                baskv
            )

        val heatmap: Array<NodeField<*>> =
            arrayOf(
                vm, va
            )

        val selected: Array<NodeField<*>> =
            arrayOf(baskv, vm, va)
    }
}

class FunctionalNodeField<T>(
    override val name: String,
    private val type: KClass<out Any>,
    private val value: (Node) -> T,
    private val formattedValue: ((T) -> String?)? = null,
    private val color: ((Node, PaletteType) -> String?)? = null,
    private val size: ((Node) -> Double?)? = null,
    private val normalized: ((Node) -> Double?)? = null,
    override val icon: String = "text_fields_icon",
    override val longName: String = name
) : NodeField<T> {
    override fun getValue(row: Node): T {
        return value.invoke(row)
    }

    override fun getFormattedValue(row: Node): String? {
        if (formattedValue != null) {
            val value: T = getValue(row)
            return formattedValue.invoke(value)
        } else {
            return super.getFormattedValue(row)
        }
    }

    override fun getColor(row: Node, paletteType: PaletteType): String? {
        return color?.invoke(row, paletteType)
    }

    override fun getNormalized(row: Node): Double? {
        return normalized?.invoke(row)
    }

    override fun toString(): String {
        return longName
    }
}